import React, { useState } from "react";
import Modal from "./Modal";
import { Download } from "lucide-react";
import { btn } from "../utils/buttonStyles";
import { useApi } from "../hooks/useApi";
import { apiError, saveBlob } from "../utils/nercAssets";

/**
 * CSV import for the NERC inventory: download the template, upload a file, read the preview
 * (what each row will do, and why a row fails), then import. The import is all or nothing: one row
 * with an error means nothing is saved. The server refuses a file other than the one previewed.
 */

const ACTION_TONES = {
  Create: "bg-green-50 text-green-700 border-green-200",
  Update: "bg-blue-50 text-blue-700 border-blue-200",
  Unchanged: "bg-gray-50 text-gray-600 border-gray-200",
  Error: "bg-red-50 text-red-700 border-red-200",
};

export default function NercCsvImportModal({ onClose, onImported }) {
  const apiCall = useApi();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errorsOnly, setErrorsOnly] = useState(false);

  const form = (extra = {}) => {
    const data = new FormData();
    data.append("file", file);
    Object.entries(extra).forEach(([k, v]) => data.append(k, v));
    return data;
  };

  const downloadTemplate = async () => {
    try {
      const blob = await apiCall("GET", "/nerc-assets/import/template", null, false, "blob");
      if (blob) saveBlob(blob, "nerc-cip-002-inventory-template.csv");
    } catch (err) { setError(apiError(err, "Could not download the template.")); }
  };

  const runPreview = async () => {
    if (!file) { setError("Choose a CSV file."); return; }
    setBusy(true); setError(null); setPreview(null);
    try { setPreview(await apiCall("POST", "/nerc-assets/import/preview", form(), true)); }
    catch (err) { setError(apiError(err, "Could not read the file.")); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setError(null);
    try {
      const result = await apiCall("POST", "/nerc-assets/import/commit", form({ previewSha256: preview.fileSha256 }), true);
      onImported(result);
    } catch (err) {
      setError(apiError(err, "The import failed. Nothing was saved."));
    } finally { setBusy(false); }
  };

  const rows = (preview?.rows || []).filter((r) => !errorsOnly || r.action === "Error");

  return (
    <Modal isOpen onClose={onClose} title="Import NERC inventory from CSV" size="3xl">
      <div className="space-y-4" data-testid="nerc-import">
        <div className="text-sm text-gray-600 space-y-1">
          <p>One row per site, ESP, BES Cyber System or Cyber Asset. Rows are matched by name (a site by its name, everything else by site and name), so importing again updates what is already there.</p>
          <p>Owners, notes and Defender device links are not in the file and are left as they are. Up to 5,000 rows and 2 MB per file.</p>
          <button type="button" onClick={downloadTemplate} className={btn("secondary")}><Download className="h-4 w-4" />Download the template</button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input type="file" accept=".csv,text/csv" data-testid="nerc-import-file"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setError(null); }} className="text-sm" />
          <button type="button" onClick={runPreview} disabled={!file || busy}
            className="px-3 py-2 text-sm rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
            {busy && !preview ? "Reading…" : "Preview"}
          </button>
        </div>

        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">{error}</p>}
        {preview?.fileError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">{preview.fileError}</p>}

        {preview && !preview.fileError && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="nerc-import-summary">
              <span><strong>{preview.creates}</strong> to create</span>
              <span><strong>{preview.updates}</strong> to update</span>
              <span><strong>{preview.unchanged}</strong> unchanged</span>
              <span className={preview.errors ? "text-red-700" : ""}><strong>{preview.errors}</strong> with errors</span>
              {preview.errors > 0 && (
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} /> Show errors only</label>
              )}
            </div>
            {!preview.canCommit && preview.errors > 0 && (
              <p className="text-sm text-red-700">Fix the rows with errors and preview again. Nothing is imported while any row has an error.</p>
            )}
            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white"><tr className="text-left text-xs text-gray-500"><th className="px-3 py-2">Line</th><th>Type</th><th>Site</th><th>Name</th><th>Result</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.line} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-1.5 text-gray-500">{r.line}</td>
                      <td>{r.recordType}</td>
                      <td>{r.site || "—"}</td>
                      <td>{r.name || "—"}</td>
                      <td className="py-1.5">
                        <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold ${ACTION_TONES[r.action] || ""}`}>{r.action}</span>
                        {r.errors.map((e) => <span key={e} className="block text-xs text-red-700 mt-1">{e}</span>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn("neutral", "md")}>Cancel</button>
          <button type="button" onClick={commit} disabled={!preview?.canCommit || busy} data-testid="nerc-import-commit"
            className={btn("primary", "md")}>
            {busy && preview ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
